import React, { useCallback, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import SearchPage from '@/components/Pages/SearchPage';
import { useFileStore, useModalStore, useAuthStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import ListItem from '@/components/ListItem';
import SignInCTAPanel from '@/components/Placeholders/SignInCTAPanel';
import { getFileIconByExtension, isImageFile, getFileTypeLabel, formatFileSize, getStatusText } from '@/constants/utils/files';

const styles = StyleSheet.create({
  emptyRoot: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  emptyIconWrap: {
    width: 88,
    height: 88,
    borderRadius: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    marginTop: 20,
    textAlign: 'center',
  },
  emptySubtitle: {
    marginTop: 8,
    textAlign: 'center',
    lineHeight: 22,
    opacity: 0.9,
  },
});

const FileSearchPage = React.memo(() => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const userId = useAuthStore(state => state.user?.id);
  const hasUser = Boolean(userId);

  // File store state
  const files = useFileStore(state => state.files);
  const searchFileIds = useFileStore(state => state.searchFileIds);
  const isLoading = useFileStore(state => state.isLoading);
  const searchError = useFileStore(state => state.errors.search);

  // Actions
  const { searchFiles, clearSearch } = useFileStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);

  const handleDetailsModal = useCallback((fileId) => {
    openModal(
      'FileEditModal',
      {
        key: `file-details-${fileId}`,
        fileId,
      },
      {
        width: isXS ? '100%' : 600,
        fullScreen: isXS
      }
    );
  }, [openModal, isXS]);

  const renderFileItem = useCallback((fileId) => {
    const file = files[fileId];
    if (!file) return null;

    const iconName = getFileIconByExtension(file.extension || '');
    const sizeLabel = formatFileSize(file.sizeInBytes || 0);
    const typeLabel = getFileTypeLabel(file.mimeType || file.fileType || '');
    const imageUrl = isImageFile(file.extension) ? file.thumbnailPath : null;

    const subtitle = (
      <Text color='secondary' size='m' numberOfLines={1}>
        {typeLabel}{sizeLabel ? ` • ${sizeLabel}` : ''}
      </Text>
    );

    return (
      <ListItem
        key={file.id}
        isSquare={true}
        iconName={iconName || 'file'}
        imageUrl={imageUrl}
        title={file.name || file.originalName || 'Untitled'}
        subtitle={subtitle}
        isXS={isXS}
        numberOfLines={1}
        onPress={() => handleDetailsModal(file.id)}
      />
    );
  }, [files, isXS, handleDetailsModal, colors]);

  const searchHandler = useCallback(async (text) => {
    return searchFiles(text, 1, null);
  }, [searchFiles]);

  const renderEmpty = useCallback(() => {
    const iconSize = 40;
    const iconColor = colors.text_color_tertiary;
    return (
      <View style={[styles.emptyRoot, { paddingVertical: 48 }]}>
        <View style={[styles.emptyIconWrap, { backgroundColor: colors.solid_color_tertiary }]}>
          <Icon name="folder-open" size={iconSize} color={iconColor} />
        </View>
        <Text
          size="l"
          weight="semibold"
          color="primary"
          style={styles.emptyTitle}
        >
          {t('pages.files.searchEmptyTitle')}
        </Text>
        <Text
          size="m"
          color="secondary"
          style={styles.emptySubtitle}
        >
          {t('pages.files.searchEmptySubtitle')}
        </Text>
      </View>
    );
  }, [colors]);

  const searchPageProps = useMemo(() => ({
    title: t('pages.files.searchTitle'),
    description: t('pages.files.searchDescription'),
    searchPlaceholder: t('pages.files.searchPlaceholderFull'),
    cancelRoute: '/files',
    isSearching: isLoading,
    searchError,
    searchResults: searchFileIds,
    onSearch: searchHandler,
    onClearSearch: clearSearch,
    renderItem: renderFileItem,
    emptyMessage: t('pages.files.searchEmptyMessage'),
    renderEmpty,
  }), [t, isLoading, searchError, searchFileIds, searchHandler, clearSearch, renderFileItem, renderEmpty]);

  // Anonymous visitors: `searchFiles` targets the user's library, so we
  // replace the entire search shell with a sign-in CTA panel rather
  // than letting them type into a search that always 401s.
  if (!hasUser) {
    return (
      <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
        <SignInCTAPanel
          icon="folder-magnifying-glass"
          source="files_search"
          title="Sign in to search your files"
          subtitle="File search runs against your personal library, so you'll need an account before we can find anything."
        />
      </View>
    );
  }

  return <SearchPage {...searchPageProps} />;
});

function FileSearchRoute() {
  return (
    <FileSearchPage />
  );
}

export default React.memo(FileSearchRoute);
